import { formatCentsAsDollars, parseDollarsToCents } from './money';

// How one payment's total is split across property lines and the "Not for the properties" bucket
// (AD-18 D8a, .claude/rules/allocation.md). The system never chooses a property allocation — the
// user's typed amount, percentage or preset is the confirmation. Sales tax pro-rata is the one
// allocation the system makes.
//
// All money is integer cents and every percentage a whole number. A share is always an integer
// product divided by an integer and floored (Math.floor(x * p / q)); nothing here multiplies by a
// fraction or divides dollars.

// Rounding remainders go to 2301 (the Phase 2b convention). Matched on the address the API
// returns, never on a hard-coded id.
export const REMAINDER_ADDRESS_PREFIX = '2301 ';
const FIFTY_FIFTY_SECOND_PREFIX = '2303 ';

export interface AllocationLineInput {
  id: string; // the wizard line's key; ties results back to the screen
  propertyAddress: string;
}

export interface AmountsInput {
  mode: 'amounts';
  totalCents: number;
  lines: (AllocationLineInput & { amountCents: number | null })[]; // null = blank
  personalCents: number | null; // null = blank
  salesTaxText: string; // as typed; blank = no sales tax
}

export interface PercentagesInput {
  mode: 'percentages';
  totalCents: number;
  lines: (AllocationLineInput & { percent: number | null })[]; // null = blank
  personalPercent: number | null;
}

export type BlockReason =
  | 'under-assigned'
  | 'over-assigned'
  | 'line-not-positive'
  | 'no-property-line'
  | 'sales-tax-invalid'
  | 'nothing-before-tax';

export interface AllocatedLine {
  id: string;
  preTaxCents: number | null; // the typed amount when sales tax applies; null otherwise
  taxShareCents: number;
  remainderCents: number;
  writtenCents: number; // what the payload sends
}

export interface AllocationResult {
  lines: AllocatedLine[]; // screen order
  personal: { preTaxCents: number | null; taxShareCents: number; writtenCents: number };
  remainderLineId: string | null; // set only when a remainder was assigned
  remaining: { cents: number; percent: number | null }; // percent only in percentages mode
  blocked: BlockReason[]; // empty = Save allowed
}

// Plain-language text for each blocking reason, shown wherever Next or Save is disabled.
export const BLOCK_REASON_TEXT: Record<BlockReason, string> = {
  'under-assigned': 'Some of the total is not assigned yet.',
  'over-assigned': 'The parts add up to more than the total.',
  'line-not-positive': 'Every property line needs an amount above $0.00.',
  'no-property-line': 'Add at least one property line.',
  'sales-tax-invalid': "The sales tax amount isn't a valid dollar amount.",
  'nothing-before-tax': 'Enter what each part cost before tax.',
};

// Among lines on the 2301 property, the one with the largest amount before the remainder; if none
// is on 2301, the largest property line. Ties go to the earliest line on screen. The personal
// bucket is never a candidate.
export function pickRemainderLine(
  lines: { id: string; propertyAddress: string; preRemainderCents: number }[]
): string | null {
  const on2301 = lines.filter((l) => l.propertyAddress.startsWith(REMAINDER_ADDRESS_PREFIX));
  const pool = on2301.length > 0 ? on2301 : lines;
  let best: (typeof lines)[number] | null = null;
  for (const line of pool) {
    if (best === null || line.preRemainderCents > best.preRemainderCents) best = line;
  }
  return best ? best.id : null;
}

// The 50/50 preset: one line on the 2301 property and one on the 2303 property, 50 each. Null when
// either property is missing, so the preset is not offered.
export function fiftyFiftyPreset(
  properties: { id: string; address: string }[]
): { propertyId: string; percent: 50 }[] | null {
  const first = properties.find((p) => p.address.startsWith(REMAINDER_ADDRESS_PREFIX));
  const second = properties.find((p) => p.address.startsWith(FIFTY_FIFTY_SECOND_PREFIX));
  if (!first || !second) return null;
  return [
    { propertyId: first.id, percent: 50 },
    { propertyId: second.id, percent: 50 },
  ];
}

// The payment memo, sent only when part of the total is not for the properties.
export function composeMemo(personalCents: number, totalCents: number): string {
  return `Not for the properties: $${formatCentsAsDollars(personalCents)} of $${formatCentsAsDollars(totalCents)} total`;
}

export function parsePercent(text: string): number | null {
  const trimmed = text.trim();
  if (!/^\d+$/.test(trimmed)) return null;
  return parseInt(trimmed, 10);
}

export function allocate(input: AmountsInput | PercentagesInput): AllocationResult {
  return input.mode === 'amounts' ? allocateAmounts(input) : allocatePercentages(input);
}

function allocateAmounts(input: AmountsInput): AllocationResult {
  const blocked: BlockReason[] = [];
  const total = input.totalCents;

  let tax = 0;
  const taxText = input.salesTaxText.trim();
  if (taxText !== '') {
    const parsed = parseDollarsToCents(taxText);
    if (parsed === null) blocked.push('sales-tax-invalid');
    else tax = parsed;
  }

  const typed = input.lines.map((l) => l.amountCents ?? 0);
  const typedPersonal = input.personalCents ?? 0;
  const before = typed.reduce((s, a) => s + a, 0) + typedPersonal; // A

  // Shares need A > 0. With tax but nothing before tax there is nothing to divide by.
  const nothingBeforeTax = tax > 0 && before === 0;
  if (nothingBeforeTax) blocked.push('nothing-before-tax');
  const shareTax = tax > 0 && !nothingBeforeTax;

  const shares = typed.map((a) => (shareTax ? Math.floor((tax * a) / before) : 0));
  const personalShare = shareTax ? Math.floor((tax * typedPersonal) / before) : 0;
  const remainder = shareTax ? tax - shares.reduce((s, x) => s + x, 0) - personalShare : 0;

  const remainderLineId =
    remainder > 0
      ? pickRemainderLine(
          input.lines.map((l, i) => ({
            id: l.id,
            propertyAddress: l.propertyAddress,
            preRemainderCents: typed[i] + shares[i],
          }))
        )
      : null;

  const lines: AllocatedLine[] = input.lines.map((l, i) => {
    const remainderCents = l.id === remainderLineId ? remainder : 0;
    return {
      id: l.id,
      preTaxCents: tax > 0 ? typed[i] : null,
      taxShareCents: shares[i],
      remainderCents,
      writtenCents: typed[i] + shares[i] + remainderCents,
    };
  });
  const personal = {
    preTaxCents: tax > 0 ? typedPersonal : null,
    taxShareCents: personalShare,
    writtenCents: typedPersonal + personalShare,
  };

  const remainingCents = total - (before + tax);
  return finish(input.lines.length, lines, personal, remainderLineId, {
    remaining: { cents: remainingCents, percent: null },
    total,
    blocked,
    complete: !nothingBeforeTax && !blocked.includes('sales-tax-invalid'),
  });
}

function allocatePercentages(input: PercentagesInput): AllocationResult {
  const total = input.totalCents;
  const percents = input.lines.map((l) => l.percent ?? 0);
  const personalPercent = input.personalPercent ?? 0;
  const percentSum = percents.reduce((s, p) => s + p, 0) + personalPercent;

  const shares = percents.map((p) => Math.floor((total * p) / 100));
  const personalCents = Math.floor((total * personalPercent) / 100);
  // The rounding remainder exists only once everything is assigned; before that the gap is
  // "remaining to assign", not a remainder.
  const remainder =
    percentSum === 100 ? total - shares.reduce((s, x) => s + x, 0) - personalCents : 0;

  const remainderLineId =
    remainder > 0
      ? pickRemainderLine(
          input.lines.map((l, i) => ({
            id: l.id,
            propertyAddress: l.propertyAddress,
            preRemainderCents: shares[i],
          }))
        )
      : null;

  const lines: AllocatedLine[] = input.lines.map((l, i) => {
    const remainderCents = l.id === remainderLineId ? remainder : 0;
    return {
      id: l.id,
      preTaxCents: null,
      taxShareCents: 0,
      remainderCents,
      writtenCents: shares[i] + remainderCents,
    };
  });
  const personal = { preTaxCents: null, taxShareCents: 0, writtenCents: personalCents };

  return finish(input.lines.length, lines, personal, remainderLineId, {
    remaining: {
      cents: total - Math.floor((total * percentSum) / 100),
      percent: 100 - percentSum,
    },
    total,
    blocked: [],
    complete: true,
  });
}

function finish(
  lineCount: number,
  lines: AllocatedLine[],
  personal: AllocationResult['personal'],
  remainderLineId: string | null,
  ctx: {
    remaining: AllocationResult['remaining'];
    total: number;
    blocked: BlockReason[];
    complete: boolean;
  }
): AllocationResult {
  const blocked = [...ctx.blocked];
  const gap = ctx.remaining.percent ?? ctx.remaining.cents;
  if (gap > 0) blocked.push('under-assigned');
  if (gap < 0) blocked.push('over-assigned');
  if (lineCount === 0) blocked.push('no-property-line');
  if (lines.some((l) => l.writtenCents <= 0)) blocked.push('line-not-positive');

  // With nothing left to assign, the parts must add back to the total exactly.
  if (ctx.complete && gap === 0 && lineCount > 0) {
    const written = lines.reduce((s, l) => s + l.writtenCents, 0) + personal.writtenCents;
    if (written !== ctx.total) {
      throw new Error(`Allocation invariant failed: ${written} written against ${ctx.total}`);
    }
  }

  return { lines, personal, remainderLineId, remaining: ctx.remaining, blocked };
}
